/** The booking record editor's binding of the view-agnostic record core
 *  (`features/records/editor`): everything FieldSlot and the Ledger need to know about a
 *  booking that the catalog alone does not say.
 *
 *  - Verdicts per record: the booking and its guest from edit_context `restech.verdicts`,
 *    each party member from its own `party[].verdicts` — the server's, as they stand.
 *    The check-in pause (D8) is said in the server's words only: whether a check-in
 *    pauses this booking's feed, and whether a bookings sync still imports it past the
 *    pause (today every integration whose webhooks pause has one, so the server reads
 *    `may_overwrite` for what a sync writes), is the resolver's to say. So a check-in
 *    queued in the draft does not turn fields "suspended" here: a changed field reads the
 *    verdict the preview plans for its change (FieldSlot / the ledger row), and the
 *    ledger banner quotes the server's check-in verdict. A queued Clear / No-show of a
 *    booking the server reports paused previews the lift. booking_state keeps its own
 *    verdict.
 *  - Actions: offered only where they can run — the catalog action available, the kill
 *    switch, console_bookings_edit, the action's permissions (console_bookings_checkin for
 *    check-in), its scope (check-in and change owner on the opened booking; pre-check-in
 *    per participant) and its guards (`blocked`). A Clear / No-show of a check-in the
 *    reservation system recorded warns that its next redemption webhook re-applies it. An
 *    email contact another user already has (`email_in_use`) offers "make that user the
 *    owner" — the conflicting contact edit dropped, the owner change queued.
 *  - The guest's contacts (O8): email and phone are edited through the contacts list, the
 *    person record's field; removing or disabling the contact that is the guest's email /
 *    phone raises "Which contact becomes the user email?" — answered by an enabled row of
 *    the draft (`isOptionAsked` / `resolveOption`, the server's rule).
 *  - Editability: the kill switch, console_bookings_edit, the guest fields edit_context
 *    left out for this caller (`guest.omitted_fields`), the field's extra permissions
 *    (console_user_manage for every guest field) and the guards edit_context `blocked`
 *    reports for this record.
 *  - Ledger groups: the booking · the guest "affects all N bookings" · each party
 *    member (its booking and its user together).
 *  - Pickers: the batch edit's tour and role searches with their tenant + pk identity
 *    (`tourMeta` / `roleMeta`), and the tag search limited to the categories edit_context
 *    assigns to the field.
 *  - Preview and apply: `useBookingChangesetPreview` / `useApplyBookingChangeset`.
 */
import { useMemo } from 'react';

import {
  EVENT_STATUS_LABELS,
  rolePickerOptions,
  tourPickerOptions,
  type BookingEditRoleOption,
  type BookingEditTourOption,
} from '@/api/bookingEdit';
import {
  useApplyBookingChangeset,
  useBookingChangesetPreview,
  type BookingRecordCatalog,
  type BookingRecordEditContext,
  type BookingRecordVerdict,
} from '@/api/bookingRecord';
import { tagOptions, type BookingDetail } from '@/api/bookings';
import { roleMeta, tourMeta } from '@/features/bookings/edit/FieldBlock';
import { actionPath, recordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';
import type {
  RecordAction,
  RecordEditorConfig,
  RecordField,
  RecordFieldConflict,
  RecordFix,
  RecordGroup,
  RecordOption,
  RecordOptionRef,
  RecordSuspension,
  RecordVerdict,
  RecordWidget,
} from '@/features/records/editor/types';
import {
  asContactsValue,
  contactsPreviewBody,
  CONTACTS_FIELD,
  revertContact,
  userAddressAnswers,
  userAddressAsked,
  type ContactsValue,
  type UserAddressType,
} from '@/features/records/fields/widgets/contactsModel';
import { createRecordPicker, createTagsWidget } from '@/features/records/fields/widgets/pickers';
import { EDITING_OFF } from '@/features/records/header/editingOff';

import { createChangeOwnerWidget } from './ChangeOwnerWidget';
import { saveDeliveriesHref } from './receiptLinks';

/** Group_Tour_User.STATE_CANCELLED. */
export const BOOKING_STATE_CANCELLED = 2;
/** The changeset option of the guest event switch (events.guest_events: none | emit). */
export const GUEST_EVENTS_OPTION = 'guest_events';
/** Group_Tour_User.CHECKIN_RESTECH — the check-in came from the reservation system. */
export const CHECKIN_SOURCE_RESTECH = 1;

const CHECKIN_LABELS: Record<string, string> = {
  unknown: 'Not checked in',
  checked_in: 'Checked in',
  no_show: 'No-show',
  error: 'Check-in error',
  interactive: 'Interactive check-in',
  pending: 'Check-in pending',
  retry_later: 'Retry later',
};

const PRECHECKIN_LABELS: Record<string, string> = {
  not_started: 'Not started',
  partial: 'Partial',
  complete: 'Complete',
  skipped: 'Skipped',
};

/** Fields that move the start — a date change fires `date_changed`. */
const START_FIELDS = new Set(['tour', 'date', 'departure_time']);

/** The contacts questions, by the contact type they decide. */
const ADDRESS_OPTIONS: Record<string, UserAddressType> = { user_email: 'email', user_phone: 'phone' };

/** The contacts value `to` without the edit that conflicts on row `key`: the row back to
 *  what is stored (an added row goes). A conflict on the user email moving onto a row —
 *  that address signs another user in — takes the whole list back, the move with it. */
export function withoutContactEdit(to: ContactsValue, stored: ContactsValue, key: string | null | undefined): ContactsValue {
  if (!key) return stored;
  const next = revertContact(to, stored, key);
  return next.user_email === key && stored.user_email !== key ? stored : next;
}

/** The first sentence of a server sentence-list ("…; clearing lifts the pause. A check-in
 *  that came from …") — what concerns a check-in the console queues. */
function firstSentence(text: string): string {
  const m = /^(.*?\.)\s+(?=[A-Z])/.exec(text);
  return m ? m[1]! : text;
}

function userName(u: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ');
}

function tourRef(t: BookingEditTourOption): RecordOptionRef {
  const meta = tourMeta(t);
  return { id: t.id, label: t.name, tenant: meta.tenant, extra: meta.extra };
}

function roleRef(r: BookingEditRoleOption): RecordOptionRef {
  const meta = roleMeta(r);
  return { id: r.id, label: r.name, tenant: meta.tenant };
}

/** A check-in queued on the lead: `checkin` suspends feed updates, `clear` (Clear
 *  check-in or No-show) lifts a suspension. */
export function queuedCheckin(state: DraftState, leadRec: RecordKey): 'checkin' | 'clear' | null {
  const entry = state.entries[leadRec]?.[actionPath('checkin')];
  if (!entry) return null;
  if (entry.to === 'checked_in') return 'checkin';
  if (entry.to === 'unknown' || entry.to === 'no_show') return 'clear';
  return null;
}

export interface BookingEditorConfigInput {
  catalog: BookingRecordCatalog;
  editContext: BookingRecordEditContext;
  /** The booking detail — names of the booking's current tags; where its check-in came
   *  from. */
  detail?: (Pick<BookingDetail, 'tags'> & Partial<Pick<BookingDetail, 'checkin_source'>>) | null;
  /** A ledger row for a field not on screen (another page tab). */
  onFocusMissing?: (rec: RecordKey, path: string) => void;
}

/** The pure half of the config (everything but the two hooks), for tests and reuse. */
export function bookingEditorConfigParts({
  catalog,
  editContext: ctx,
  detail,
  onFocusMissing,
}: BookingEditorConfigInput): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets' | 'actionWidgets'> {
  const leadRec = recordKey('booking', ctx.booking_id);
  const guestRec = ctx.guest ? recordKey('user', ctx.guest.user_id) : null;
  const restech = ctx.restech;
  const managedBy = restech.managed_by ? (restech.managed_by_display ?? restech.managed_by) : null;
  const name = managedBy ?? 'The feed';

  const partyVerdicts = new Map<RecordKey, Record<string, BookingRecordVerdict>>();
  const partyGroup = new Map<RecordKey, RecordGroup>();
  for (const m of ctx.party) {
    const title = `Party: ${userName(m.user) || 'Unnamed'} (#${m.booking_id})`;
    const group = { id: `party:${m.booking_id}`, title };
    partyVerdicts.set(recordKey('booking', m.booking_id), m.verdicts);
    partyGroup.set(recordKey('booking', m.booking_id), group);
    if (m.user) {
      partyVerdicts.set(recordKey('user', m.user.id), m.verdicts);
      partyGroup.set(recordKey('user', m.user.id), group);
    }
  }

  const verdictOf = (rec: RecordKey, field: RecordField, state: DraftState): RecordVerdict | null => {
    if (!managedBy) return null;
    const base =
      rec === leadRec || rec === guestRec ? restech.verdicts[field.key] : partyVerdicts.get(rec)?.[field.key];
    if (!base || base.verdict === 'kept') return base ?? null;
    if (field.key === 'booking_state' || field.key === 'checkin_status') return base;
    // Clearing a check-in the server reports as pausing the feed lifts the pause (the
    // catalog: "clearing lifts the pause"); what the feed then does to the field is the
    // server's to say once saved.
    if (queuedCheckin(state, leadRec) === 'clear' && restech.suspended_reason && base.verdict === 'suspended') {
      return {
        verdict: 'may_overwrite',
        evidence: `${name} updates resume once the cleared check-in is saved`,
        queued: true,
      };
    }
    return base;
  };

  // The banner's info variant, in the server's words: the state verdict while the booking
  // is paused (it names the passthrough and, where one exists, the sync past the pause);
  // the check-in verdict for a queued check-in. No server statement → no pause claimed.
  const suspensionOf = (state: DraftState): RecordSuspension | null => {
    if (!managedBy) return null;
    const queued = queuedCheckin(state, leadRec);
    if (restech.suspended_reason) {
      if (queued === 'clear') return null;
      const said = restech.verdicts.booking_state?.evidence;
      return {
        queued: false,
        text: said || `${name} updates are paused while this booking is checked in; cancellations and no-shows still arrive`,
      };
    }
    if (queued !== 'checkin') return null;
    const checkin = restech.verdicts.checkin_status;
    if (!checkin || checkin.verdict === 'kept' || !checkin.evidence) return null;
    // The server's first sentence says what a check-in does to THIS feed — a pause
    // ("Checking in pauses Ventrata webhooks; a bookings sync from Ventrata can still
    // import.") or none ("A check-in does not pause Intrepid imports.").
    return { queued: true, text: `Queued check-in: ${firstSentence(checkin.evidence)}` };
  };

  const editabilityOf = (rec: RecordKey, field: RecordField) => {
    if (!ctx.flags.booking_edit_enabled || !catalog.enabled) {
      return { editable: false, reason: EDITING_OFF };
    }
    if (!ctx.permissions.console_bookings_edit) {
      return { editable: false, reason: 'Needs the console_bookings_edit permission' };
    }
    // The guest's PII the server left out for this caller: no value to start from.
    if (rec === guestRec && ctx.guest?.omitted_fields.includes(field.key)) {
      return { editable: false, reason: 'Hidden — needs the console_user_view permission' };
    }
    const missing = field.permissions.find((p) => !ctx.permissions[p]);
    if (missing) return { editable: false, reason: `Needs the ${missing} permission` };
    const name = field.kind === 'action' && field.action ? field.action : field.key;
    const guards = ctx.blocked[`${rec}:${name}`] ?? [];
    if (guards.length > 0) {
      return { editable: false, reason: guards.map((g) => catalog.guards[g] ?? g).join(' ') };
    }
    return { editable: true };
  };

  const fieldByKey = new Map<string, RecordField>(catalog.fields.map((f) => [f.key, f]));
  const partyBookings = new Set(ctx.party.map((m) => recordKey('booking', m.booking_id)));
  const actionAvailable = (action: RecordAction, rec: RecordKey): boolean => {
    if (action.available === false) return false;
    if (!rec.startsWith('booking:')) return false;
    if (action.scope === 'lead' ? rec !== leadRec : rec !== leadRec && !partyBookings.has(rec)) return false;
    if ((action.permissions ?? []).some((p) => !ctx.permissions[p])) return false;
    const field = fieldByKey.get(action.field);
    // The kill switch, console_bookings_edit, the field's permissions and the guards
    // edit_context reports for this record and action.
    return field ? editabilityOf(rec, field).editable : false;
  };

  const restechCheckin =
    detail?.checkin_source === CHECKIN_SOURCE_RESTECH && ctx.baselines[leadRec]?.checkin_status === 'checked_in';
  const changeWarnings = (rec: RecordKey, path: string, to: unknown): string[] => {
    if (rec !== leadRec || path !== actionPath('checkin') || !restechCheckin) return [];
    if (to !== 'unknown' && to !== 'no_show') return [];
    return [`${managedBy ?? 'The booking system'} recorded this check-in, so its next redemption webhook re-applies it`];
  };

  // The guest's stored contacts, and the draft's (the contacts questions and fixes).
  const storedContacts = asContactsValue(guestRec ? ctx.baselines[guestRec]?.[CONTACTS_FIELD] : null);
  const draftContacts = (state: DraftState): ContactsValue | null => {
    const entry = guestRec ? state.entries[guestRec]?.[CONTACTS_FIELD] : undefined;
    return entry ? asContactsValue(entry.to) : null;
  };

  // An email contact another user already has: the server suggests moving the booking to
  // that user instead (`suggested_action: change_owner`) — one undoable step that drops
  // the conflicting contact edit and queues the owner change.
  const changeOwner = catalog.actions.find((a) => a.key === 'change_owner');
  const fixFor = (rec: RecordKey, path: string, conflict: RecordFieldConflict, state: DraftState): RecordFix | null => {
    if (conflict.suggested_action !== 'change_owner' || !conflict.owner_user_id) return null;
    if (rec !== guestRec || path !== CONTACTS_FIELD || !changeOwner || !actionAvailable(changeOwner, leadRec)) return null;
    if (conflict.owner_user_id === ctx.baselines[leadRec]?.owner) return null;
    const to = draftContacts(state) ?? storedContacts;
    return {
      label: `Make user #${conflict.owner_user_id} the owner instead`,
      changes: [
        { rec, path, to: withoutContactEdit(to, storedContacts, conflict.contact_key) },
        { rec: leadRec, path: actionPath('change_owner'), to: conflict.owner_user_id },
      ],
    };
  };

  const groupOf = (rec: RecordKey): RecordGroup => {
    if (rec === leadRec) return { id: 'lead', title: `Booking #${ctx.booking_id}` };
    if (rec === guestRec && ctx.guest) {
      const n = ctx.guest.affects_bookings;
      return {
        id: 'guest',
        title: `Guest ${ctx.guest.display_name || `#${ctx.guest.user_id}`}`,
        note: n > 1 ? `affects all ${n} bookings of this guest` : null,
      };
    }
    return partyGroup.get(rec) ?? { id: rec, title: rec };
  };

  const recordOrder: RecordKey[] = [
    leadRec,
    ...(guestRec ? [guestRec] : []),
    ...ctx.party.flatMap((m) => [
      recordKey('booking', m.booking_id),
      ...(m.user ? [recordKey('user', m.user.id)] : []),
    ]),
  ];

  const isOptionAsked = (option: RecordOption, state: DraftState): boolean | undefined => {
    const entries = Object.entries(state.entries);
    if (option.key === 'cancel_mode') {
      return entries.some(
        ([rec, paths]) => rec.startsWith('booking:') && paths.booking_state?.to === BOOKING_STATE_CANCELLED,
      );
    }
    // The guest's contacts questions: asked when the user email / phone row is removed or
    // disabled and the draft names no other (the server's rule).
    const type = ADDRESS_OPTIONS[option.key];
    if (type) {
      const to = draftContacts(state);
      return to ? userAddressAsked(storedContacts, to, type) : false;
    }
    return undefined;
  };

  // A contacts question offers the enabled rows of its type the draft keeps — or, for the
  // phone, "No user phone".
  const resolveOption = (option: RecordOption, state: DraftState): RecordOption => {
    const type = ADDRESS_OPTIONS[option.key];
    if (!type) return option;
    const to = draftContacts(state) ?? storedContacts;
    return { ...option, values: [...userAddressAnswers(to, type), ...option.values] };
  };

  const formatValue = (field: RecordField, _rec: RecordKey, value: unknown): string | undefined => {
    if (field.key === 'role' && (value === null || value === undefined)) return 'Guest (no staff role)';
    if (field.key === 'checkin_status' && typeof value === 'string') return CHECKIN_LABELS[value];
    if (field.key === 'precheckin_state' && typeof value === 'string') return PRECHECKIN_LABELS[value];
    return undefined;
  };

  const tagRefs: RecordOptionRef[] = (detail?.tags ?? []).map((t) => ({
    id: t.id,
    label: t.name,
    group: t.category?.name ?? null,
    tenant: ctx.tenant.code,
  }));
  const optionRefs: Record<string, RecordOptionRef[]> = {
    tags: tagRefs,
    precheckin_tags: tagRefs,
    ...(ctx.tour
      ? {
          tour: [
            {
              id: ctx.tour.id,
              label: ctx.tour.name ?? `#${ctx.tour.id}`,
              tenant: ctx.tenant.code,
              extra: [ctx.tour.custom_id, ctx.tour.start_timezone],
            },
          ],
        }
      : {}),
    ...(ctx.role ? { role: [{ id: ctx.role.id, label: ctx.role.name ?? `#${ctx.role.id}`, tenant: ctx.tenant.code }] } : {}),
    ...(ctx.guest
      ? { owner: [{ id: ctx.guest.user_id, label: ctx.guest.display_name || `#${ctx.guest.user_id}` }] }
      : {}),
  };

  const actionByKey = new Map<string, (typeof catalog.actions)[number]>(catalog.actions.map((a) => [a.key, a]));
  const estimateEvents = (state: DraftState, switches: Readonly<Record<string, string>>) => {
    const out: Array<{ code: string; guest_visible: boolean; suppressible?: boolean }> = [];
    for (const [rec, paths] of Object.entries(state.entries)) {
      if (rec.startsWith('user:')) {
        // Only when chosen (events.guest_events: emit) — one per changed user, its own
        // policy, not the booking's Emit · Suppress.
        if (switches[GUEST_EVENTS_OPTION] === 'emit') {
          out.push({ code: 'user.updated', guest_visible: false, suppressible: false });
        }
        continue;
      }
      if (!rec.startsWith('booking:')) continue;
      for (const [path, entry] of Object.entries(paths)) {
        if (path.startsWith('action:')) {
          const action = actionByKey.get(path.slice('action:'.length));
          // An action's own update event is not what reaches the guest.
          for (const code of action?.events ?? []) {
            out.push({ code, guest_visible: Boolean(action?.guest_visible) && !code.endsWith('.updated') });
          }
          continue;
        }
        if (path === 'booking_state' && entry.to === BOOKING_STATE_CANCELLED) {
          out.push({ code: 'tour_participant.deleted', guest_visible: true, suppressible: false });
          continue;
        }
        out.push({ code: 'tour_participant.updated', guest_visible: false });
        if (START_FIELDS.has(path)) out.push({ code: 'tour_participant.date_changed', guest_visible: true });
      }
    }
    return out;
  };

  return {
    fields: catalog.fields,
    options: catalog.options,
    actions: catalog.actions,
    effects: catalog.effects,
    optionDefaults: ctx.option_defaults,
    timezone: ctx.schedule.timezone,
    recordNoun: 'booking',
    managedBy,
    verdictOf,
    suspensionOf,
    suspendedText: () => `${name} updates are paused while this booking is checked in; cancellations and no-shows still arrive`,
    editabilityOf,
    actionAvailable,
    changeWarnings,
    fixFor,
    groupOf,
    recordOrder,
    isOptionAsked,
    resolveOption,
    formatValue,
    provenanceOf: (field) =>
      field.kind === 'vendor' ? `Set by ${managedBy ?? 'the booking system'}` : field.help || field.restech_note || undefined,
    optionRefs,
    estimateEvents,
    confirmFollowUps: ['cancel_chain'],
    deliveriesHref: saveDeliveriesHref,
    // Guest-only changes emit nothing by default (user decision, 2026-10-01).
    entityEventSwitch: { option: GUEST_EVENTS_OPTION, entity: 'user', group: 'guest', valueLabels: { none: 'None' } },
    eventStatusLabel: (s) => EVENT_STATUS_LABELS[s] ?? s.replace(/_/g, ' '),
    maxChanges: catalog.max_changes,
    descriptionMaxLength: catalog.description_max_length,
    restechHelpStory: 'booking-detail',
    ...(onFocusMissing ? { onFocusMissing } : {}),
  };
}

/** The booking's remote pickers, scoped to its tenant (and, for tags, to the categories
 *  edit_context assigns to each field). */
export function bookingWidgets(ctx: Pick<BookingRecordEditContext, 'tenant' | 'tag_categories'>): Record<string, RecordWidget> {
  const tenantId = ctx.tenant.id;
  const categoriesOf = (fieldKey: string) =>
    new Set(ctx.tag_categories.filter((c) => c.field === fieldKey).map((c) => c.id));
  return {
    tour: createRecordPicker({
      load: (_f, _r, q, signal) => tourPickerOptions(q, tenantId, signal).then((rows) => rows.map(tourRef)),
      placeholder: 'Type a tour name or id',
    }),
    role: createRecordPicker({
      load: (_f, _r, q, signal) => rolePickerOptions(q, tenantId, signal).then((rows) => rows.map(roleRef)),
      placeholder: 'Guest (no staff role)',
      fetchOnEmpty: true,
    }),
    tags: createTagsWidget({
      load: async (field, _r, q, signal) => {
        const allowed = categoriesOf(field.key);
        const rows = await tagOptions(tenantId, q, undefined, signal);
        return rows
          .filter((t) => allowed.size === 0 || allowed.has(t.category.id))
          .map((t) => ({
            id: t.id,
            label: t.name,
            group: t.category.name,
            tenant: t.tenant?.code ?? null,
          }));
      },
    }),
  };
}

/** The booking editor's `RecordEditorConfig` — stable while its inputs are. */
export function useBookingRecordEditorConfig(input: BookingEditorConfigInput): RecordEditorConfig {
  const { catalog, editContext, detail, onFocusMissing } = input;
  const id = editContext.booking_id;
  const tenantId = editContext.tenant.id;
  const categoriesKey = JSON.stringify(editContext.tag_categories.map((c) => [c.id, c.field]));
  const widgets = useMemo(
    () => bookingWidgets({ tenant: editContext.tenant, tag_categories: editContext.tag_categories }),
    // The pickers depend on the tenant and the category assignment only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, categoriesKey],
  );
  // A franchise guest belongs to the parent operator: owners are searched there.
  const ownerTenantId = editContext.guest?.tenant_id ?? tenantId;
  const actionWidgets = useMemo(() => ({ user: createChangeOwnerWidget({ tenantId: ownerTenantId }) }), [ownerTenantId]);
  return useMemo<RecordEditorConfig>(
    () => ({
      ...bookingEditorConfigParts({ catalog, editContext, detail, onFocusMissing }),
      widgets,
      actionWidgets,
      // The guest's contacts rows still being typed are not asked about (as on the user record).
      usePreview: (body) => useBookingChangesetPreview(id, contactsPreviewBody(body)),
      useApply: (opts) => useApplyBookingChangeset(id, opts),
    }),
    [catalog, editContext, detail, onFocusMissing, widgets, actionWidgets, id],
  );
}
